import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment, hasPaymentsConfigured } from "@/lib/stripe";
import { toast } from "@/hooks/use-toast";

export type CheckoutTier = "self_service" | "with_call" | "extension_7_days";

/**
 * Avvia un checkout V2 e restituisce il clientSecret per l'embedded checkout.
 * Usato dalle superfici diverse da /v2/pricing (es. estensione Sprint).
 */
export function useV2Checkout() {
  const [busyTier, setBusyTier] = useState<CheckoutTier | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const closeCheckout = useCallback(() => setClientSecret(null), []);

  const startCheckout = useCallback(async (tier: CheckoutTier) => {
    if (!hasPaymentsConfigured()) {
      toast({
        title: "Pagamenti non disponibili",
        description: "Configurazione Stripe mancante.",
        variant: "destructive",
      });
      return;
    }
    setBusyTier(tier);
    try {
      const returnUrl = `${window.location.origin}/v2/checkout/success?session_id={CHECKOUT_SESSION_ID}`;
      const { data, error } = await supabase.functions.invoke("v2-create-checkout", {
        body: { tier, environment: getStripeEnvironment(), returnUrl },
      });
      if (error) throw error;
      if (data?.type === "checkout" && data.clientSecret) {
        setClientSecret(data.clientSecret);
        return;
      }
      if (data?.type === "no_sprint_to_extend") {
        toast({
          title: "Nessuno Sprint da estendere",
          description: "Acquista prima uno Sprint.",
          variant: "destructive",
        });
        return;
      }
      throw new Error("Unexpected response");
    } catch (e) {
      console.error(e);
      toast({
        title: "Errore",
        description: "Non siamo riusciti ad aprire il pagamento. Riprova.",
        variant: "destructive",
      });
    } finally {
      setBusyTier(null);
    }
  }, []);

  return { busyTier, clientSecret, startCheckout, closeCheckout };
}
